import { useEffect, useMemo, useRef, useState } from "react";
import RetailVsDistribuidorChart from "../components/RetailVsDistribuidorChart";
import DealersExternalTable from "../components/DealersExternalTable";
import api from "../lib/api";
import PdfExportButton from "../components/PdfExportButton";
import { Users, Car, MapPin, Link as LinkIcon, RefreshCw, Copy, Check, Info, Zap, Mail, Phone, ChevronDown, ChevronUp, Download } from "lucide-react";
import { AIInsightsPanel, AIChatWidget } from "../components/AIPanels";
import InfoTip from "../components/InfoTip";
import MultiSelect from "../components/MultiSelect";
import useNormalizedFilter from "../hooks/useNormalizedFilter";

const MONTHS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const fmt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));
const fmtPct = (n, d = 1) => `${(n ?? 0).toFixed(d)}%`;

const CHANNEL_COLORS = [
  "#f4c400", "#1f2937", "#06b6d4", "#a855f7",
  "#22c55e", "#f97316", "#ec4899", "#6b7280", "#0ea5e9", "#84cc16",
];

// Capture-quality funnel — colors per stage
const STAGE_COLORS = {
  "Lead Recibido": "#111827",
  "Con Contacto":  "#3b82f6",
  "Con Interés":   "#a855f7",
  "Con Modelo":    "#f97316",
  "Con Sucursal":  "#16a34a",
};

// Tooltips explaining each calculation
const TIPS = {
  total:        "Total de leads digitales recibidos vía Zapier en el período/filtros aplicados.",
  with_model:   "% de leads donde el campo modelo está presente (ej: Tracker, Silverado). Fórmula: leads_con_modelo / total × 100. Indica calidad del formulario.",
  with_branch:  "% de leads donde el campo sucursal está presente. Fórmula: leads_con_sucursal / total × 100. Indica si el formulario pide preferencia geográfica.",
  with_utm:     "% de leads atribuibles a una campaña/canal (utm_source poblado). Fórmula: leads_con_utm / leads_atribuibles × 100. Los leads que caen en el canal 'Web' es porque no llegaron con UTM identificable para atribuirlos a una campaña, por eso se excluyen del denominador (no son atribuibles por definición).",
  channels:     "Distribución de leads por canal digital (derivado del utm_source). Meta = facebook/instagram, Google = google/googleads, etc. 'Web' agrupa los leads que llegaron sin UTM identificable, por lo que no se les pudo atribuir una campaña.",
  trend:        "Volumen mensual de leads por año. Útil para detectar estacionalidad y comparar performance YoY.",
  hourly:       "Distribución de leads por hora del día (horario Santo Domingo, UTC-4). Útil para planificar dotación del equipo comercial en horas pico. Respeta todos los filtros activos.",
  campaigns:    "Top 8 campañas por volumen de leads. Click para filtrar todo el dashboard por esa campaña.",
  brands:       "Top marcas que generan más interés digital. Solo se cuentan leads con el campo marca presente.",
  branches:     "Top puntos de venta. Auto Asesores y Gonzauto se consolidan bajo 'Red de Dealers Externos' para una vista de portafolio limpia. Los filtros sí permiten seleccionar cada uno por separado.",
  quality:      "Quality Score = promedio de % con contacto + % con marca + % con sucursal. ≥90% verde, 70-90% ámbar, <70% rojo.",
};

export default function LeadsPerformancePage() {
  const pageRef = useRef(null);
  const [opts, setOpts] = useState({ years: [], brands: [], branches: [], channels: [], campaigns: [], statuses: [], source_types: [], business_units: [] });
  // Filters model: array per key so MultiSelect can emit/receive multiple
  // values. Empty array = no filter. Persisted in localStorage per dashboard
  // so navigating away and back preserves the user's view.
  const DEFAULT_FILTERS = { year: [], month: [], brand: [], branch: [], channel: [], campaign: [], source_type: [], business_unit: [] };
  const { filters, setFilters, setF, toggleF, reset } = useNormalizedFilter(
    "lp_filters_v2", DEFAULT_FILTERS,
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dealersBreakdown, setDealersBreakdown] = useState(null);
  const [expandedChannel, setExpandedChannel] = useState(null);
  const toggleExpandChannel = (id) => setExpandedChannel((cur) => (cur === id ? null : id));

  // Build ?year=X,Y query params: only non-empty arrays are serialised.
  const buildParams = (f) => Object.fromEntries(
    Object.entries(f)
      .filter(([, v]) => Array.isArray(v) ? v.length > 0 : (v && v !== "all"))
      .map(([k, v]) => [k, Array.isArray(v) ? v.join(",") : v])
  );

  useEffect(() => {
    api.get("/leads-digital/options", { params: buildParams(filters) })
       .then((r) => setOpts(r.data || {})).catch(() => {});
  }, [filters]);

  // Auto-heal stale filter values. If localStorage was seeded with values
  // that no longer exist in the current options (e.g. a year 2027 from
  // an older dataset, or a business_unit that got renamed), silently drop
  // them so the user isn't stuck with a query that returns 0 rows.
  const optsLoaded = (opts.years?.length || 0) > 0;
  useEffect(() => {
    if (!optsLoaded) return;
    const rowValue = (o) => (o && typeof o === "object" && "value" in o ? o.value : o);
    const validSets = {
      year:          new Set((opts.years || []).map((o) => String(rowValue(o)))),
      month:         new Set((opts.months || []).map((o) => String(rowValue(o)))),
      brand:         new Set((opts.brands || []).map((o) => String(rowValue(o)))),
      branch:        new Set((opts.branches || []).map((o) => String(rowValue(o)))),
      channel:       new Set((opts.channels || []).map((o) => String(rowValue(o)))),
      campaign:      new Set((opts.campaigns || []).map((o) => String(rowValue(o)))),
      source_type:   new Set((opts.source_types || []).map((o) => String(rowValue(o)))),
      business_unit: new Set((opts.business_units || []).map((o) => String(rowValue(o)))),
    };
    setFilters((cur) => {
      let dirty = false;
      const next = { ...cur };
      for (const [k, valid] of Object.entries(validSets)) {
        if (valid.size === 0) continue; // don't wipe if backend returned nothing
        const arr = Array.isArray(cur[k]) ? cur[k] : [];
        const cleaned = arr.filter((v) => valid.has(String(v)));
        if (cleaned.length !== arr.length) {
          next[k] = cleaned;
          dirty = true;
        }
      }
      return dirty ? next : cur;
    });
  }, [optsLoaded]);

  useEffect(() => {
    setLoading(true);
    api.get("/leads-digital/summary", { params: buildParams(filters) })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    api.get("/leads-digital/dealers-breakdown", { params: buildParams(filters) })
      .then((r) => setDealersBreakdown(r.data))
      .catch(() => {});
  }, [filters]);

  const activeFilterCount = Object.values(filters).filter((v) => Array.isArray(v) ? v.length > 0 : (v && v !== "all")).length;
  const dealerParams = useMemo(() => buildParams(filters), [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Solo este mes" quick toggle — sets year+month to current
  const now = new Date();
  const currentYear = String(now.getFullYear());
  const currentMonth = String(now.getMonth() + 1);
  const isThisMonth = filters.year?.includes?.(currentYear) && filters.month?.includes?.(currentMonth);
  const toggleThisMonth = () => {
    if (isThisMonth) {
      setFilters((f) => ({ ...f, year: [], month: [] }));
    } else {
      setFilters((f) => ({ ...f, year: [currentYear], month: [currentMonth] }));
    }
  };

  // Filters for AI panel — flat single-value strings for backward compat.
  const aiFilters = useMemo(() =>
    Object.fromEntries(
      Object.entries(filters)
        .filter(([, v]) => Array.isArray(v) ? v.length > 0 : (v && v !== "all"))
        .map(([k, v]) => [k, Array.isArray(v) ? v.join(",") : v])
    ),
    [filters]
  );

  if (loading && !data) {
    return (
      <div className="lp2-wrap">
        <Header total={null} onReset={reset} activeFilters={0}/>
        <div className="surface p-12 text-center"><div className="spinner mx-auto" style={{ borderColor: "#11182733", borderTopColor: "#111827" }}/></div>
      </div>
    );
  }
  if (!data || data.total === 0) {
    return (
      <div className="lp2-wrap">
        <Header total={0} onReset={reset} activeFilters={activeFilterCount}/>
        <div className="surface p-12 text-center font-semibold text-gray-500">
          Sin datos digitales todavía. Configura el webhook de Zapier en <a className="font-bold underline" href="/admin/integrations">Admin · Integraciones</a> para empezar.
        </div>
      </div>
    );
  }

  const kpis = data.kpis;
  const channels = (data.channels || []).slice(0, 8).map((c, i) => ({ ...c, color: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }));
  let acc = 0;
  const donutStops = channels.map((c) => { const s = acc; acc += c.pct; return `${c.color} ${s}% ${acc}%`; }).join(", ");
  const donutBg = channels.length ? `conic-gradient(${donutStops})` : "conic-gradient(#f4c400 0% 100%)";

  return (
    <div ref={pageRef} className="lp2-wrap" data-testid="leads-performance">
      <Header total={data.total} pageRef={pageRef} onReset={reset} activeFilters={activeFilterCount} isThisMonth={isThisMonth} onToggleThisMonth={toggleThisMonth} csvParams={dealerParams}/>

      {/* Filter grid — collapsible on mobile via <details>. On desktop it's
          always visible; on mobile it opens as a drawer. */}
      <details className="lp2-filters-details surface p-4 mb-5" data-testid="lp2-filters"
               open={typeof window !== "undefined" && window.innerWidth >= 768}>
        <summary className="lp2-filters-summary">
          <span className="lp2-filters-summary-label">
            Filtros{activeFilterCount ? ` (${activeFilterCount})` : ""}
          </span>
          <ChevronDown size={16} className="lp2-filters-summary-chev"/>
        </summary>
        <div className="lp2-filter-grid">
          {[
            ["Año",           "year",          opts.years || []],
            ["Mes",           "month",         MONTHS_ES.map((m, i) => ({ value: String(i+1), label: m }))],
            ["Marca",         "brand",         opts.brands || []],
            ["Punto de Venta","branch",        opts.branches || []],
            ["Unidad",        "business_unit", opts.business_units || []],
            ["Origen Form",   "source_type",   (opts.source_types || []).map(s => ({ value: s.value, label: s.label, count: s.count }))],
            ["Canal",         "channel",       opts.channels || []],
            ["Campaña",       "campaign",      opts.campaigns || []],
          ].map(([label, key, options]) => (
            <div key={key}>
              <label className="label-up block mb-2">{label}</label>
              <MultiSelect
                value={filters[key]}
                onChange={(v) => setF(key, v)}
                placeholder={label}
                options={options}
                testid={`lp2-f-${key}`}
              />
            </div>
          ))}
        </div>
      </details>

      {/* KPIs — capture quality metrics (2 cols mobile / 4 cols desktop) */}
      <section className="lp2-kpi-grid mb-5" data-testid="lp2-kpis">
        <KpiCard icon={<Users size={18}/>}    label="Total Leads"    value={fmt(kpis.total)}          accent="#111827" tip={TIPS.total}/>
        <KpiCard icon={<Car size={18}/>}      label="Con Modelo"     value={fmtPct(kpis.model_rate)}  accent="#f97316" tip={TIPS.with_model}/>
        <KpiCard icon={<MapPin size={18}/>}   label="Con Sucursal"   value={fmtPct(kpis.branch_rate)} accent="#16a34a" tip={TIPS.with_branch}/>
        <KpiCard icon={<LinkIcon size={18}/>} label="Atribución" value={fmtPct(kpis.utm_rate)}    accent="#f4c400" tip={TIPS.with_utm}/>
      </section>

      {/* AI Insights */}
      <AIInsightsPanel
        filters={aiFilters}
        endpoint="/leads-digital/insights"
        method="get"
        title="Insights con IA · Performance Digital"
        subtitle="Análisis generado por Claude Sonnet 4.5 sobre los leads digitales filtrados."
        storageKey="ai_digital_insights_collapsed"
      />

      {/* Two-column layout: Business Unit donut (left) + Channel breakdown (right).
          Business Unit is placed first so Retail/Distribuidor sits in the
          primary eye-line of the dashboard. */}
      <div className="lp2-two-col mb-5">
        <BusinessUnitPanel
          data={data.business_units || { items: [], unassigned: 0, unassigned_pct: 0 }}
          total={data.total}
          active={(filters.business_unit || [])[0] || "all"}
          onToggle={(v) => toggleF("business_unit", v)}
        />

        {/* Channel donut card */}
        <section className="surface p-4 md:p-6" data-testid="lp2-channels">
          <SectionHead title="Origen de Leads por Canal" sub={`${channels.length} fuentes digitales activas`} tip={TIPS.channels}/>
          <div className="lp2-donut-layout">
            <div className="lp2-donut-box">
              <div className="lp2-donut-ring" style={{ background: donutBg }}/>
              <div className="lp2-donut-center">
                <div className="lp2-donut-big">{fmt(data.total)}</div>
                <div className="lp2-donut-small">{(filters.channel || []).length === 0 ? "Total leads" : filters.channel}</div>
              </div>
            </div>
            <div className="lp2-channel-list">
              {channels.map((c) => {
                const srcBreak = c.breakdown_source_type || [];
                const cmpBreak = c.breakdown_campaign || [];
                const isExpanded = expandedChannel === c.id;
                const hasBreakdown = srcBreak.length > 0 || cmpBreak.length > 0;
                return (
                  <div key={c.id} className="lp2-channel-block">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleF("channel", c.id)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleF("channel", c.id); } }}
                      className={`lp2-channel-row ${(filters.channel || []).includes(c.id) ? "is-active" : ""} ${(filters.channel || []).length > 0 && !(filters.channel || []).includes(c.id) ? "is-dimmed" : ""}`}
                      data-testid={`lp2-channel-${c.id}`}
                    >
                      <span className="lp2-channel-dot" style={{ background: c.color }}/>
                      <span className="lp2-channel-name truncate flex items-center gap-1">{c.id}</span>
                      <span className="lp2-channel-value">{fmt(c.value)}</span>
                      <span className="lp2-channel-pct">{c.pct}%</span>
                      {hasBreakdown && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleExpandChannel(c.id); }}
                          aria-label={`Ver desglose de ${c.id}`}
                          aria-expanded={isExpanded}
                          className="lp2-channel-toggle"
                          data-testid={`lp2-channel-tip-${c.id}`}
                          title={isExpanded ? "Ocultar desglose" : "Ver desglose por sub-fuente"}
                        >
                          {isExpanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                        </button>
                      )}
                    </div>
                    {isExpanded && (
                      <div className="lp2-channel-breakdown" data-testid={`lp2-channel-breakdown-${c.id}`}>
                        {srcBreak.length > 0 && (
                          <>
                            <div className="lp2-channel-breakdown-hdr">Por tipo de fuente</div>
                            {srcBreak.slice(0, 5).map((s) => (
                              <div key={`src-${s.name}`} className="lp2-channel-breakdown-row">
                                <span className="truncate" title={s.name}>{s.name}</span>
                                <span className="lp2-channel-breakdown-n">{fmt(s.n)}</span>
                                <span className="lp2-channel-breakdown-pct">{s.pct}%</span>
                              </div>
                            ))}
                          </>
                        )}
                        {cmpBreak.length > 0 && (
                          <>
                            <div className="lp2-channel-breakdown-hdr" style={{ marginTop: 10 }}>
                              Top campañas <span className="lp2-channel-breakdown-sub">· click para filtrar</span>
                            </div>
                            {cmpBreak.slice(0, 5).map((s) => {
                              const isActive = (filters.campaign || []).includes(s.name);
                              return (
                                <button
                                  key={`cmp-${s.name}`}
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); toggleF("campaign", s.name); }}
                                  data-testid={`lp2-channel-tip-cmp-${s.name}`}
                                  className={`lp2-channel-breakdown-row lp2-channel-breakdown-clickable ${isActive ? "is-active" : ""}`}
                                  title={`Filtrar dashboard por campaña: ${s.name}`}
                                >
                                  <span className="truncate">{s.name}</span>
                                  <span className="lp2-channel-breakdown-n">{fmt(s.n)}</span>
                                  <span className="lp2-channel-breakdown-pct">{s.pct}%</span>
                                </button>
                              );
                            })}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Right column: Channel breakdown (already opened above at start).
            Moved BusinessUnitPanel out and placed it as the left column
            of this same two-col grid — see the top of this section. */}
      </div>

      {/* Trend + Hourly — dos charts temporales lado a lado */}
      <div className="lp2-two-col mb-5">
        <section className="surface p-4 md:p-6" data-testid="lp2-trend">
          <SectionHead title="Tendencia Mensual" sub="Volumen de leads digitales por año" tip={TIPS.trend}/>
          <TrendChart months={data.trend.months} series={data.trend.series} colors={data.trend.colors}/>
        </section>

        {data.hourly && data.hourly.total > 0 ? (
          <section className="surface p-4 md:p-6" data-testid="lp2-hourly">
            <SectionHead
              title="Distribución por Hora del Día"
              sub={`${data.hourly.total.toLocaleString("es-DO")} leads · Pico ${formatHour(data.hourly.peak_hour)} (${((data.hourly.peak_count / data.hourly.total) * 100).toFixed(1)}%)`}
              tip={TIPS.hourly}
            />
            <HourlyChart buckets={data.hourly.buckets} peakHour={data.hourly.peak_hour}/>
          </section>
        ) : <div/>}
      </div>

      <RetailVsDistribuidorChart params={buildParams(filters)} onMonthClick={(ym) => {
        const [y, m] = ym.split("-"); const mo = String(+m);
        setFilters((f) => (f.year?.length === 1 && f.year[0] === y && f.month?.length === 1 && f.month[0] === mo
          ? { ...f, year: [], month: [] } : { ...f, year: [y], month: [mo] }));
      }}/>

      {/* Distribution row: Campañas, Marcas, Sucursales */}
      <div className="grid gap-5 mb-5" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <section className="surface p-6" data-testid="lp2-campaigns">
          <SectionHead title="Top Campañas" sub="Volumen de leads" tip={TIPS.campaigns}/>
          <BarList items={data.campaigns} active={(filters.campaign || [])[0] || "all"} onClick={(id) => toggleF("campaign", id)}/>
        </section>
        <section className="surface p-6" data-testid="lp2-brands">
          <SectionHead title="Top Marcas" sub="Interés en marca" tip={TIPS.brands}/>
          <BarList items={data.brands} active={(filters.brand || [])[0] || "all"} onClick={(id) => toggleF("brand", id)}/>
        </section>
        <section className="surface p-6" data-testid="lp2-branches">
          <SectionHead title="Top Puntos de Venta" sub="Distribución geográfica" tip={TIPS.branches}/>
          <BarList
            items={(data.branches || []).map((b) => ({ ...b, id: b.id.split(" - ")[0] || b.id }))}
            active={((filters.branch || [])[0] || "all") === "all" ? "all" : ((filters.branch || [])[0] || "").split(" - ")[0]}
            onClick={(id) => {
              const full = (data.branches || []).find((b) => (b.id.split(" - ")[0] || b.id) === id);
              toggleF("branch", full ? full.id : id);
            }}
            dealersBreakdown={dealersBreakdown}
          />
        </section>
      </div>

      {/* Performance table — desktop shows table; mobile shows stacked cards */}
      <DealersExternalTable data={dealersBreakdown} params={dealerParams}/>
      <section className="surface p-4 md:p-6 mb-5" data-testid="lp2-table">
        <SectionHead title="Calidad de Captura por Canal · Campaña" sub="Top 10 combinaciones — % con cada campo + UTM más frecuente" tip={TIPS.quality}/>
        {/* Desktop / tablet table view */}
        <div className="lp2-perf-table-wrap">
          <table className="lp2-perf-table">
            <thead>
              <tr>
                <th>Canal</th>
                <th>Campaña</th>
                <th style={{ textAlign: "right" }}>Leads</th>
                <th>UTM</th>
                <th style={{ textAlign: "right" }}>Contacto</th>
                <th style={{ textAlign: "right" }}>Marca</th>
                <th style={{ textAlign: "right" }}>Punto de Venta</th>
                <th style={{ textAlign: "right" }}>Quality</th>
              </tr>
            </thead>
            <tbody>
              {(data.table || []).map((r, i) => (
                <tr key={i}>
                  <td>
                    <strong>{r.channel}</strong>
                    {r.channel_source && (
                      <div className="text-[10px] text-gray-500 font-medium mt-0.5"
                           title={`Sitio origen inferido: ${r.channel_source}`}>
                        {r.channel_source}
                      </div>
                    )}
                  </td>
                  <td className="truncate" style={{ maxWidth: 180 }} title={r.campaign}>{r.campaign}</td>
                  <td style={{ textAlign: "right" }}>{fmt(r.leads)}</td>
                  <td>
                    <UtmLinkCell utm_source={r.utm_source} utm_medium={r.utm_medium} utm_campaign={r.utm_campaign} utm_query={r.utm_query}/>
                  </td>
                  <td style={{ textAlign: "right" }}><RatePill v={r.contact_rate}/></td>
                  <td style={{ textAlign: "right" }}><RatePill v={r.brand_rate}/></td>
                  <td style={{ textAlign: "right" }}><RatePill v={r.branch_rate}/></td>
                  <td style={{ textAlign: "right" }}>
                    <span className="pill" style={{ background: r.quality_score >= 90 ? "#dcfce7" : r.quality_score >= 75 ? "#fef3c7" : "#fee2e2",
                                                    color: r.quality_score >= 90 ? "#166534" : r.quality_score >= 75 ? "#92400e" : "#991b1b",
                                                    fontSize: 11, fontWeight: 800 }}>{r.quality_score}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile card view — same data, easier to scan on a phone */}
        <div className="lp2-perf-cards" data-testid="lp2-table-cards">
          {(data.table || []).map((r, i) => (
            <article key={i} className="lp2-perf-card">
              <header className="lp2-perf-card__head">
                <div className="lp2-perf-card__title">
                  <div className="lp2-perf-card__channel">{r.channel}</div>
                  {r.channel_source && (
                    <div className="lp2-perf-card__src" title={`Sitio origen: ${r.channel_source}`}>
                      {r.channel_source}
                    </div>
                  )}
                  <div className="lp2-perf-card__campaign" title={r.campaign}>{r.campaign}</div>
                </div>
                <span className="pill" style={{
                  background: r.quality_score >= 90 ? "#dcfce7" : r.quality_score >= 75 ? "#fef3c7" : "#fee2e2",
                  color:      r.quality_score >= 90 ? "#166534" : r.quality_score >= 75 ? "#92400e" : "#991b1b",
                  fontSize: 12, fontWeight: 800,
                }}>Quality {r.quality_score}%</span>
              </header>
              <div className="lp2-perf-card__grid">
                <div>
                  <div className="lp2-perf-card__label">Leads</div>
                  <div className="lp2-perf-card__value">{fmt(r.leads)}</div>
                </div>
                <div>
                  <div className="lp2-perf-card__label">Contacto</div>
                  <div className="lp2-perf-card__value"><RatePill v={r.contact_rate}/></div>
                </div>
                <div>
                  <div className="lp2-perf-card__label">Marca</div>
                  <div className="lp2-perf-card__value"><RatePill v={r.brand_rate}/></div>
                </div>
                <div>
                  <div className="lp2-perf-card__label">Punto de Venta</div>
                  <div className="lp2-perf-card__value"><RatePill v={r.branch_rate}/></div>
                </div>
              </div>
              <div className="lp2-perf-card__utm">
                <div className="lp2-perf-card__label">UTM</div>
                <UtmLinkCell utm_source={r.utm_source} utm_medium={r.utm_medium} utm_campaign={r.utm_campaign} utm_query={r.utm_query}/>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* AI Chat widget */}
      <AIChatWidget
        filters={aiFilters}
        endpoint="/ai/digital-chat"
        scope="digital"
        title="Asistente Digital · Claude 4.5"
      />
    </div>
  );
}

function RealtimeLeadFeed() {
  const [open, setOpen] = useState(true);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const r = await api.get("/leads-digital/recent", { params: { limit: 8 } });
      setLeads(r.data.items || []);
      setLastFetch(new Date());
    } catch (e) { /* noop */ } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchLeads();
    const iv = setInterval(fetchLeads, 30000); // poll every 30s
    return () => clearInterval(iv);
  }, []);

  const channelColor = (ch) => {
    const map = { "Meta Ads": "#1877f2", "Google Ads": "#4285f4", "TikTok Ads": "#ee1d52",
                  "YouTube": "#ff0000", "Email Marketing": "#f97316", "WhatsApp": "#25d366",
                  "Referido": "#a855f7", "Orgánico": "#16a34a", "Web": "#6b7280" };
    return map[ch] || "#f4c400";
  };

  const formatRelative = (iso) => {
    if (!iso) return "—";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "ahora";
    if (diff < 3600) return `${Math.floor(diff/60)}m`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h`;
    return `${Math.floor(diff/86400)}d`;
  };

  return (
    <div className={`feed-widget ${open ? "is-open" : ""}`} data-testid="realtime-feed">
      <button className="feed-toggle" onClick={() => setOpen(!open)} aria-label="Toggle feed" data-testid="realtime-feed-toggle">
        <Zap size={16}/>
        <span className="feed-badge">{leads.length}</span>
      </button>
      {open && (
        <div className="feed-panel">
          <div className="feed-header">
            <div>
              <div className="font-display font-extrabold text-sm flex items-center gap-2">
                <Zap size={14} style={{ color: "var(--accent)" }}/> Live Leads
              </div>
              <div className="text-xs text-gray-500 font-semibold mt-1">
                {lastFetch ? `Actualizado · ${formatRelative(lastFetch.toISOString())}` : "—"}
                · auto cada 30s
              </div>
            </div>
            <button className="feed-refresh" onClick={fetchLeads} disabled={loading} aria-label="Refrescar" data-testid="realtime-feed-refresh">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""}/>
            </button>
          </div>
          <div className="feed-list">
            {leads.length === 0 && !loading && (
              <div className="text-xs text-gray-400 font-semibold text-center py-6">
                Sin leads recibidos aún.
              </div>
            )}
            {leads.map((l, i) => (
              <div key={i} className="feed-item" data-testid={`feed-item-${i}`}>
                <div className="feed-item-head">
                  <span className="feed-item-dot" style={{ background: channelColor(l.channel) }}/>
                  <span className="feed-item-name truncate" title={`${l.first_name || ""} ${l.last_name || ""}`}>
                    {(l.first_name || "—") + " " + (l.last_name || "")}
                  </span>
                  <span className="feed-item-time">{formatRelative(l.created_at)}</span>
                </div>
                <div className="feed-item-meta">
                  <span className="feed-item-channel">{l.channel || "—"}</span>
                  {l.brand && <span className="feed-item-tag">{l.brand}</span>}
                  {l.model && <span className="feed-item-tag muted">{l.model}</span>}
                </div>
                <div className="feed-item-contact">
                  {l.email && <span title={l.email}><Mail size={11}/></span>}
                  {l.phone && <span title={l.phone}><Phone size={11}/></span>}
                  {l.branch && <span className="truncate" title={l.branch}><MapPin size={11}/> {l.branch.split(" - ")[0]}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RatePill({ v }) {
  const bg = v >= 90 ? "#dcfce7" : v >= 70 ? "#fef3c7" : "#fee2e2";
  const color = v >= 90 ? "#166534" : v >= 70 ? "#92400e" : "#991b1b";
  return <span className="pill" style={{ background: bg, color, fontSize: 11 }}>{v}%</span>;
}

function UtmLinkCell({ utm_source, utm_medium, utm_campaign, utm_query }) {
  const [copied, setCopied] = useState(false);
  if (!utm_source && !utm_campaign) {
    return <span className="text-xs text-gray-400 font-semibold">— sin UTM</span>;
  }
  const fullLink = utm_query ? `?${utm_query}` : "";
  const copy = (e) => {
    e.stopPropagation();
    if (!fullLink) return;
    navigator.clipboard.writeText(fullLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="utm-cell">
      <div className="utm-tags">
        {utm_source && <span className="utm-tag utm-source" title={`utm_source · canal real`}>{utm_source}</span>}
        {utm_medium && <span className="utm-tag utm-medium" title="utm_medium">{utm_medium}</span>}
        {utm_campaign && <span className="utm-tag utm-campaign" title="utm_campaign · campaign ID">{utm_campaign}</span>}
      </div>
      {fullLink && (
        <button
          className="utm-copy-btn"
          onClick={copy}
          title={`Copiar query string: ${fullLink}`}
          aria-label="Copiar UTM query string"
        >
          {copied ? <><Check size={11}/> Copiado</> : <><Copy size={11}/> {fullLink.length > 28 ? fullLink.slice(0, 28) + "…" : fullLink}</>}
        </button>
      )}
    </div>
  );
}

function LeadsCsvButton({ params }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/leads-digital/export-csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `leads_performance_${new Date().toISOString().slice(0, 10)}.csv` });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } finally { setBusy(false); }
  };
  return (
    <button className="btn flex items-center gap-2 whitespace-nowrap" onClick={download} disabled={busy} data-testid="lp2-csv-btn">
      <Download size={14}/> {busy ? "Descargando..." : "CSV"}
    </button>
  );
}

function Header({ total, onReset, activeFilters, isThisMonth, onToggleThisMonth, pageRef, csvParams }) {
  const now = new Date();
  const monthName = now.toLocaleDateString("es-DO", { month: "long", year: "numeric" });
  return (
    <div className="lp2-header">
      <div>
        <h1 className="font-display text-2xl font-extrabold">Lead Generation Analysis</h1>
        <p className="text-sm text-gray-500 font-semibold mt-1">
          Fuente · Zapier · Canales digitales {total !== null && total !== undefined ? `· ${fmt(total)} leads en vista` : ""}
          {activeFilters > 0 && <span className="ml-2 pill" style={{ background: "var(--accent-soft)", color: "var(--dark)", fontSize: 11 }}>{activeFilters} filtros activos</span>}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2" data-pdf-hide>
        <PdfExportButton
          targetRef={pageRef}
          filename="leads-performance"
          title="SDM · Lead Generation Analysis"
          subtitle={activeFilters > 0 ? `${activeFilters} filtros activos` : ""}
          testid="lp2-pdf-btn"
        />
        {csvParams && <LeadsCsvButton params={csvParams}/>}
        <button
          className={`lp2-month-toggle ${isThisMonth ? "is-active" : ""}`}
          onClick={onToggleThisMonth}
          data-testid="lp2-toggle-this-month"
          title={`Filtrar solo leads de ${monthName}`}
        >
          {isThisMonth ? "✓ " : ""}Solo este mes
        </button>
        <button className="btn flex items-center gap-2" onClick={onReset} data-testid="lp2-reset">
          <RefreshCw size={14}/> Borrar Filtros
        </button>
      </div>
    </div>
  );
}

function SectionHead({ title, sub, tip }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <div className="font-display text-base font-extrabold leading-tight">{title}</div>
        {sub && <div className="text-xs font-semibold text-gray-500 mt-1">{sub}</div>}
      </div>
      {tip && <InfoTip>{tip}</InfoTip>}
    </div>
  );
}

function KpiCard({ icon, label, value, accent = "#111827", tip }) {
  return (
    <div className="kpi-card surface relative" data-testid={`lp2-kpi-${label}`}>
      <span className="kpi-accent" style={{ background: accent }}/>
      <div className="p-5 relative">
        <div className="flex items-start justify-between mb-3">
          <div className="kpi-icon-clean" style={{ color: accent, boxShadow: `inset 0 0 0 1px ${accent}1f` }}>{icon}</div>
          {tip && <InfoTip>{tip}</InfoTip>}
        </div>
        <div className="label-up mb-2">{label}</div>
        <div className="font-display font-extrabold leading-none truncate" style={{ fontSize: 28, letterSpacing: "-0.02em" }} title={value}>{value}</div>
      </div>
    </div>
  );
}

function Select({ value, onChange, placeholder, options, testid }) {
  const fmtN = (n) => new Intl.NumberFormat("es-DO").format(n);
  const opts = (options || []).map((o) => {
    if (typeof o === "string") return { value: o, label: o };
    if (o.label) return o; // already formatted (e.g. month selector, source_type list)
    if (typeof o.count === "number" && o.count > 0)
      return { value: o.value, label: `${o.value} (${fmtN(o.count)})` };
    return { value: o.value, label: o.value };
  });
  // Preserve the current selection even if it's no longer in the cascading options list
  const isStale = value && value !== "all" && !opts.some((o) => String(o.value) === String(value));
  return (
    <select className="lp2-select" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="all">{placeholder}</option>
      {isStale && <option value={value}>{value} (sin datos)</option>}
      {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function BarList({ items = [], active = "all", onClick, dealersBreakdown = null }) {
  const max = Math.max(...items.map((d) => d.value), 1);
  return (
    <div className="flex flex-col gap-2">
      {items.map((d) => {
        const isDealers = dealersBreakdown && /red\s*de\s*dealers/i.test(d.id);
        return (
          <BarRow
            key={d.id}
            d={d}
            max={max}
            active={active}
            onClick={onClick}
            dealersData={isDealers ? dealersBreakdown : null}
          />
        );
      })}
    </div>
  );
}

function BarRow({ d, max, active, onClick, dealersData }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        className={`lp2-bar-row ${active === d.id ? "is-active" : ""} ${active !== "all" && active !== d.id ? "is-dimmed" : ""}`}
        onClick={() => onClick && onClick(d.id)}
      >
        <div className="lp2-bar-name truncate flex items-center gap-1" title={d.id}>
          {d.id}
          {dealersData && <Info size={11} style={{ opacity: 0.5, flexShrink: 0 }}/>}
        </div>
        <div className="lp2-bar-track"><div className="lp2-bar-fill" style={{ width: `${Math.max(4, (d.value / max) * 100)}%` }}/></div>
        <div className="lp2-bar-value">{fmt(d.value)}</div>
      </button>
      {dealersData && hover && (
        <DealersTooltip data={dealersData}/>
      )}
    </div>
  );
}

function DealersTooltip({ data }) {
  const total = data.total || 1;
  return (
    <div
      className="absolute z-20 surface p-3"
      style={{
        top: "calc(100% + 4px)", left: "50%", transform: "translateX(-50%)",
        minWidth: 260, background: "#0f172a", color: "#fff", border: "1px solid #1e293b",
        boxShadow: "0 10px 25px rgba(0,0,0,0.25)", borderRadius: 10,
      }}
      data-testid="dealers-tooltip"
    >
      <div className="text-xs font-bold mb-2" style={{ color: "#fde047" }}>
        {data.bucket || "Red de Dealers Externos"} · {fmt(data.total)} leads
      </div>
      <div className="space-y-1">
        {(data.dealers || []).map((dl) => {
          const pct = ((dl.count / total) * 100).toFixed(0);
          return (
            <div key={dl.dealer_name} className="flex items-center gap-2 text-xs">
              <div className="flex-1 truncate" style={{ color: "#e2e8f0" }}>{dl.dealer_name}</div>
              <div style={{ width: 60, height: 4, background: "#1e293b", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: "#fde047" }}/>
              </div>
              <div className="font-bold" style={{ width: 30, textAlign: "right" }}>{dl.count}</div>
              <div className="text-xs" style={{ width: 30, textAlign: "right", color: "#94a3b8" }}>{pct}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrendChart({ months, series, colors }) {
  const years = useMemo(() => Object.keys(series || {}).sort(), [series]);
  const [hover, setHover] = useState(null);   // { i: monthIdx } | null
  const svgRef = useRef(null);
  const W = 600, H = 240, left = 28, right = 12, top = 26, bottom = 40;
  const innerW = W - left - right, innerH = H - top - bottom;
  const allVals = years.flatMap((y) => series[y] || []);
  const max = Math.max(...allVals, 10);
  const xAt = (i) => left + (innerW / Math.max(1, (months.length - 1))) * i;
  const yAt = (v) => top + innerH - (v / max) * innerH;
  const fmtCompact = (n) => {
    if (n == null) return "";
    if (Math.abs(n) >= 1000) {
      const val = n / 1000;
      return val >= 10 ? `${Math.round(val)}k` : `${val.toFixed(1)}k`;
    }
    return new Intl.NumberFormat("es-DO").format(n);
  };
  const fmtFull = (n) => new Intl.NumberFormat("es-DO").format(n || 0);
  // Annual mean per year — used to draw the dashed reference line.
  const meansByYear = useMemo(() => {
    const out = {};
    years.forEach((y) => {
      const vals = (series[y] || []).filter((v) => v > 0);
      out[y] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    });
    return out;
  }, [years, series]);

  // Track hover / touch to figure out which month column the user is over.
  const handlePointer = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0]?.clientX : e.clientX;
    if (clientX == null) return;
    const px = ((clientX - rect.left) / rect.width) * W;
    const step = innerW / Math.max(1, months.length - 1);
    const i = Math.max(0, Math.min(months.length - 1, Math.round((px - left) / step)));
    setHover({ i });
  };

  // Build tooltip data whenever hover changes.
  const tooltip = useMemo(() => {
    if (!hover || years.length === 0) return null;
    const { i } = hover;
    const rows = years.map((y) => ({ year: y, value: (series[y] || [])[i] || 0 }));
    // YoY delta = latest year vs previous year (if both present and prev > 0).
    let yoyText = null;
    if (rows.length >= 2) {
      const cur  = rows[rows.length - 1];
      const prev = rows[rows.length - 2];
      if (prev.value > 0) {
        const pct = ((cur.value - prev.value) / prev.value) * 100;
        const sign = pct >= 0 ? "+" : "";
        yoyText = `${sign}${pct.toFixed(0)}% vs ${months[i]} ${prev.year} (${fmtFull(prev.value)})`;
      }
    }
    return { month: months[i], rows, yoyText, xPx: xAt(i) };
  }, [hover, years, series, months, innerW]);

  return (
    <>
      <div className="flex justify-center gap-4 mb-3">
        {years.map((y) => (
          <span key={y} className="inline-flex items-center gap-2 text-xs font-extrabold text-gray-700">
            <span style={{ width: 10, height: 10, borderRadius: 2, background: colors[y] || "#f4c400", display: "inline-block" }}/>{y}
            <span className="text-[10px] font-semibold text-gray-400">· ⌀ {fmtCompact(Math.round(meansByYear[y] || 0))}</span>
          </span>
        ))}
      </div>
      <div className="lp2-trend-svg-wrap" style={{ position: "relative" }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height: 240, cursor: "crosshair", touchAction: "pan-y" }}
          onMouseMove={handlePointer}
          onMouseLeave={() => setHover(null)}
          onTouchStart={handlePointer}
          onTouchMove={handlePointer}
          onTouchEnd={() => setTimeout(() => setHover(null), 2500)}
          data-testid="lp2-trend-svg"
        >
          {[0, .25, .5, .75, 1].map((t) => (
            <line key={t} x1={left} y1={top + innerH * t} x2={W - right} y2={top + innerH * t} stroke="#e5e7eb" strokeWidth="1"/>
          ))}

          {/* Annual mean reference line (per series, dashed) */}
          {years.map((y) => {
            const mean = meansByYear[y];
            if (!mean) return null;
            return (
              <g key={`mean-${y}`}>
                <line
                  x1={left} x2={W - right}
                  y1={yAt(mean)} y2={yAt(mean)}
                  stroke={colors[y] || "#f4c400"}
                  strokeWidth="1"
                  strokeDasharray="4 4"
                  opacity="0.55"
                />
              </g>
            );
          })}

          {/* Hover vertical guide */}
          {hover && (
            <line
              x1={xAt(hover.i)} x2={xAt(hover.i)}
              y1={top} y2={top + innerH}
              stroke="#94a3b8" strokeWidth="1" strokeDasharray="2 3"
            />
          )}

          {years.map((y, yIdx) => {
            const vals = series[y] || [];
            const pts = vals.map((v, i) => `${xAt(i)},${yAt(v)}`).join(" ");
            const labelDy = yIdx % 2 === 0 ? -8 : 16;
            return (
              <g key={y}>
                <polyline fill="none" stroke={colors[y] || "#f4c400"} strokeWidth="2.5" points={pts} strokeLinecap="round" strokeLinejoin="round"/>
                {vals.map((v, i) => (
                  <g key={i}>
                    <circle
                      cx={xAt(i)}
                      cy={yAt(v)}
                      r={hover && hover.i === i ? 5 : 3.5}
                      fill={colors[y] || "#f4c400"}
                      style={{ transition: "r .15s ease" }}
                    />
                    {v > 0 && (
                      <text
                        x={xAt(i)}
                        y={yAt(v) + labelDy}
                        textAnchor="middle"
                        fontSize="10"
                        fontWeight="800"
                        fill={colors[y] || "#f4c400"}
                        style={{ paintOrder: "stroke", stroke: "#ffffff", strokeWidth: 3, strokeLinejoin: "round" }}
                      >
                        {fmtCompact(v)}
                      </text>
                    )}
                  </g>
                ))}
              </g>
            );
          })}
          {months.map((m, i) => (
            <text key={m + i} x={xAt(i)} y={H - 6} textAnchor="middle" fill="#9ca3af" fontSize="10" fontWeight="700">{m}</text>
          ))}
        </svg>

        {/* Tooltip — positioned relative to the SVG wrapper */}
        {tooltip && (
          <div
            className="lp2-trend-tooltip"
            style={{ left: `${(tooltip.xPx / W) * 100}%` }}
            data-testid="lp2-trend-tooltip"
          >
            <div className="lp2-trend-tooltip__month">{tooltip.month}</div>
            <div className="lp2-trend-tooltip__rows">
              {tooltip.rows.map((r) => (
                <div key={r.year} className="lp2-trend-tooltip__row">
                  <span className="lp2-trend-tooltip__dot" style={{ background: colors[r.year] || "#f4c400" }}/>
                  <span className="lp2-trend-tooltip__year">{r.year}</span>
                  <span className="lp2-trend-tooltip__value">{fmtFull(r.value)}</span>
                </div>
              ))}
            </div>
            {tooltip.yoyText && (
              <div className="lp2-trend-tooltip__yoy" style={{
                color: tooltip.yoyText.startsWith("-") ? "#dc2626" : "#16a34a",
              }}>
                {tooltip.yoyText}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Distribución por hora del día — 24 barras verticales.
 *
 * UI best-practices applied:
 *  - Y-axis scale labels (max + mid) so absolute values are readable at a glance.
 *  - Noon divider (12h) with "am / pm" small-caps so the timeline is orientable
 *    without color-coded background bands.
 *  - Baseline line at y=0 for visual grounding.
 *  - Hover column highlight (soft amarillo) + cursor:pointer as affordance.
 *  - Peak label always visible (`fmtFull` — human readable), plus labels on all
 *    bars ≥ 5% via `fmtCompact` for quick scanning.
 *  - 8-point spacing grid (padding multiples of 8).
 *  - Callout pill on hover (same anatomy as TrendChart tooltip).
 */
function HourlyChart({ buckets, peakHour }) {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);
  const W = 640, H = 260, left = 40, right = 16, top = 32, bottom = 44;
  const innerW = W - left - right;
  const innerH = H - top - bottom;
  const step = innerW / 24;
  const barW = Math.max(10, step - 8);
  const maxCount = buckets.reduce((m, b) => Math.max(m, b.count), 0) || 1;
  const total = buckets.reduce((s, b) => s + b.count, 0);
  const avg = total ? Math.round(total / 24) : 0;
  const midY = Math.round(maxCount / 2);

  const handlePointer = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0]?.clientX : e.clientX;
    if (clientX == null) return;
    const px = ((clientX - rect.left) / rect.width) * W - left;
    const i = Math.max(0, Math.min(23, Math.floor(px / step)));
    setHover(buckets[i]);
  };

  const fmt = (n) => new Intl.NumberFormat("es-DO").format(n || 0);

  return (
    <>
      {/* Compact legend line — pattern matches TrendChart tooltip anatomy */}
      <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 mb-2">
        <span className="inline-flex items-center gap-2 text-xs font-extrabold text-gray-700">
          <span style={{ width: 10, height: 10, borderRadius: 2, background: "#f4c400", display: "inline-block" }}/>
          Leads por hora
          <span className="text-[10px] font-semibold text-gray-400">· ⌀ {fmtCompact(avg)}/h</span>
        </span>
      </div>

      <div className="lp2-hourly-wrap">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ width: "100%", height: "auto", display: "block" }}
          data-testid="lp2-hourly-svg"
          onMouseMove={handlePointer}
          onMouseLeave={() => setHover(null)}
          onTouchStart={handlePointer}
          onTouchMove={handlePointer}
        >
          {/* Horizontal grid at 25/50/75/100% + Y-axis labels on the left */}
          {[{ t: 1, val: maxCount }, { t: 0.5, val: midY }, { t: 0, val: 0 }].map(({ t, val }) => (
            <g key={t}>
              <line
                x1={left} x2={W - right}
                y1={top + innerH * (1 - t)} y2={top + innerH * (1 - t)}
                stroke={t === 0 ? "#cbd5e1" : "#f1f5f9"}
                strokeWidth={t === 0 ? 1 : 1}
              />
              <text
                x={left - 6} y={top + innerH * (1 - t) + 3}
                textAnchor="end"
                fontSize="9.5"
                fontWeight="600"
                fill="#94a3b8"
                style={{ fontFamily: "Manrope, sans-serif", fontVariantNumeric: "tabular-nums" }}
              >
                {fmtCompact(val)}
              </text>
            </g>
          ))}

          {/* Noon divider — subtle vertical line + am/pm small caps */}
          <line
            x1={left + 12 * step} x2={left + 12 * step}
            y1={top - 4} y2={top + innerH + 4}
            stroke="#e2e8f0" strokeWidth="1" strokeDasharray="3 4"
          />
          <text
            x={left + 6 * step} y={top - 12}
            textAnchor="middle"
            fontSize="9"
            fontWeight="800"
            fill="#94a3b8"
            style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "0.16em", textTransform: "uppercase" }}
          >
            am
          </text>
          <text
            x={left + 18 * step} y={top - 12}
            textAnchor="middle"
            fontSize="9"
            fontWeight="800"
            fill="#94a3b8"
            style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "0.16em", textTransform: "uppercase" }}
          >
            pm
          </text>

          {/* Column hitboxes — invisible, full-height for easy hover */}
          {buckets.map((b, i) => (
            <rect
              key={`hit-${b.hour}`}
              x={left + i * step} y={top - 8}
              width={step} height={innerH + 16}
              fill="transparent"
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setHover(b)}
            />
          ))}

          {/* Hover column highlight — soft amarillo band */}
          {hover && (
            <rect
              x={left + hover.hour * step + 1}
              y={top - 4}
              width={step - 2}
              height={innerH + 8}
              fill="rgba(244,196,0,0.10)"
              rx={4}
              pointerEvents="none"
            />
          )}

          {/* Bars */}
          {buckets.map((b, i) => {
            const cx = left + i * step + step / 2;
            const h = Math.max(2, (b.count / maxCount) * innerH);
            const y = top + innerH - h;
            const isPeak = b.hour === peakHour;
            const isHover = hover && hover.hour === b.hour;
            return (
              <g key={`bar-${b.hour}`} pointerEvents="none">
                <rect
                  x={cx - barW / 2}
                  y={y}
                  width={barW}
                  height={h}
                  rx={4}
                  fill={isPeak ? "#f4c400" : "#fcd34d"}
                  opacity={isPeak ? 1 : (isHover ? 1 : 0.55 + b.intensity * 0.35)}
                />
                {/* Numeric label above bar — always on peak, otherwise ≥5% or hovered */}
                {(isPeak || isHover || b.pct >= 5) && (
                  <text
                    x={cx} y={y - 7}
                    textAnchor="middle"
                    fontSize="10.5"
                    fontWeight={isPeak ? 800 : 700}
                    fill={isPeak ? "#a16207" : (isHover ? "#111827" : "#64748b")}
                    style={{ fontFamily: "Manrope, sans-serif", fontVariantNumeric: "tabular-nums" }}
                  >
                    {fmtCompact(b.count)}
                  </text>
                )}
              </g>
            );
          })}

          {/* X-axis hour labels — 12h format on peak, 24h numeric elsewhere */}
          {buckets.map((b, i) => {
            const cx = left + i * step + step / 2;
            const isPeak = b.hour === peakHour;
            return (
              <text
                key={`lbl-${b.hour}`}
                x={cx} y={top + innerH + 18}
                textAnchor="middle"
                fontSize="10"
                fontWeight={isPeak ? 800 : 600}
                fill={isPeak ? "#111827" : "#94a3b8"}
                style={{ fontFamily: "Manrope, sans-serif", fontVariantNumeric: "tabular-nums" }}
              >
                {String(b.hour).padStart(2, "0")}
              </text>
            );
          })}

          {/* Axis footer label */}
          <text
            x={left + innerW / 2} y={top + innerH + 36}
            textAnchor="middle"
            fontSize="8.5"
            fontWeight="700"
            fill="#cbd5e1"
            style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "0.14em", textTransform: "uppercase" }}
          >
            Hora del día · America/Santo_Domingo
          </text>
        </svg>

        {/* Floating pill callout */}
        {hover && (
          <div className="lp2-hourly-callout" data-testid="lp2-hourly-callout">
            <span className="lp2-hourly-callout__hour">{formatHour(hover.hour)}</span>
            <span className="lp2-hourly-callout__count">{fmt(hover.count)} leads</span>
            <span className="lp2-hourly-callout__pct">{hover.pct}% del total</span>
          </div>
        )}
      </div>
    </>
  );
}

// Human-friendly 12h format for the peak-hour label.
// 0 → "12 a.m." · 13 → "1 p.m." · 20 → "8 p.m."
function formatHour(h) {
  if (h == null) return "—";
  const hh = ((h + 11) % 12) + 1;
  const suffix = h < 12 ? "a.m." : "p.m.";
  return `${hh}:00 ${suffix}`;
}

function fmtCompact(n) {
  if (n == null) return "";
  if (Math.abs(n) >= 1000) {
    const val = n / 1000;
    return val >= 10 ? `${Math.round(val)}k` : `${val.toFixed(1)}k`;
  }
  return new Intl.NumberFormat("es-DO").format(n);
}

const BU_META = {
  "Retail":       { color: "#f4c400", icon: "🏪", desc: "Punto de venta directo al consumidor" },
  "Distribuidor": { color: "#3b82f6", icon: "🚚", desc: "Red de dealers/concesionarios independientes" },
};

function BusinessUnitPanel({ data, total, active, onToggle }) {
  const items = data?.items || [];
  const unassigned = data?.unassigned || 0;
  const unassigned_pct = data?.unassigned_pct || 0;
  // Build conic-gradient: each BU + gray slice for unassigned
  let acc = 0;
  const slices = [];
  items.forEach((it) => {
    const c = BU_META[it.id]?.color || "#8b929d";
    const s = acc; acc += it.pct;
    slices.push(`${c} ${s}% ${acc}%`);
  });
  if (unassigned > 0) {
    const s = acc; acc += unassigned_pct;
    slices.push(`#e5e7eb ${s}% ${acc}%`);
  }
  const donutBg = slices.length ? `conic-gradient(${slices.join(", ")})` : "conic-gradient(#e5e7eb 0% 100%)";
  const selected = items.find((i) => i.id === active);
  const fmtN = (n) => new Intl.NumberFormat("es-DO").format(n);

  return (
    <section className="surface p-6" data-testid="lp2-business-units">
      <SectionHead
        title="Unidad de Negocio · Retail / Distribuidor / Seminuevos"
        sub={`${items.length} unidades asignadas${unassigned > 0 ? ` · ${fmtN(unassigned)} sin asignar (${unassigned_pct}%)` : ""}`}
        tip="Distribución de leads por unidad de negocio dueña de la originación. Click en una unidad filtra todo el dashboard. Los leads sin asignar pueden corregirse haciendo backfill desde Zapier."
      />
      <div className="lp2-donut-layout">
        <div className="lp2-donut-box">
          <div className="lp2-donut-ring" style={{ background: donutBg, transition: "background 0.4s" }}/>
          <div className="lp2-donut-center">
            <div className="lp2-donut-big">{fmtN(selected ? selected.value : total)}</div>
            <div className="lp2-donut-small">{selected ? selected.id : "Total leads"}</div>
          </div>
        </div>

        <div className="flex flex-col gap-3" style={{ width: "100%" }} data-testid="bu-list">
          {items.length === 0 && unassigned === 0 && (
            <div className="text-sm text-gray-500 font-semibold p-4 text-center">
              Sin datos de unidad de negocio todavía.
            </div>
          )}
          {items.map((it) => {
            const meta = BU_META[it.id] || { color: "#8b929d", icon: "🏷️", desc: "" };
            const isActive = active === it.id;
            const isDimmed = active && active !== "all" && active !== it.id;
            return (
              <button
                key={it.id}
                type="button"
                onClick={() => onToggle(it.id)}
                className="origin-row"
                data-testid={`bu-row-${it.id}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  alignItems: "center",
                  gap: 14,
                  padding: "14px 16px",
                  borderRadius: 12,
                  border: `1px solid ${isActive ? meta.color : "#f1f5f9"}`,
                  background: isActive ? `${meta.color}0d` : "#fff",
                  opacity: isDimmed ? 0.45 : 1,
                  transition: "all 0.2s",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <div className="rounded-xl flex items-center justify-center" style={{
                  width: 44, height: 44, fontSize: 22,
                  background: `${meta.color}1a`,
                }}>
                  {meta.icon}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-extrabold text-sm" style={{ color: "#111827", letterSpacing: "-0.01em" }}>{it.id}</div>
                  <div className="text-xs text-gray-500 font-semibold truncate">{meta.desc}</div>
                  {/* Mini progress bar */}
                  <div className="relative mt-2" style={{ height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden", maxWidth: 320 }}>
                    <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${it.pct}%`, background: meta.color, borderRadius: 3, transition: "width 0.4s" }}/>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-extrabold" style={{ fontSize: 22, color: "#111827", letterSpacing: "-0.02em", lineHeight: 1 }}>{fmtN(it.value)}</div>
                  <div className="text-xs font-extrabold mt-1" style={{ color: meta.color }}>{it.pct}%</div>
                </div>
              </button>
            );
          })}
          {unassigned > 0 && (
            <div
              className="origin-row"
              data-testid="bu-row-unassigned"
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                alignItems: "center",
                gap: 14,
                padding: "14px 16px",
                borderRadius: 12,
                border: "1px dashed #cbd5e1",
                background: "#f8fafc",
              }}
            >
              <div className="rounded-xl flex items-center justify-center" style={{ width: 44, height: 44, fontSize: 18, background: "#e5e7eb", color: "#64748b" }}>?</div>
              <div className="min-w-0">
                <div className="font-display font-extrabold text-sm" style={{ color: "#475569" }}>Sin asignar</div>
                <div className="text-xs text-gray-500 font-semibold">Leads sin <code className="bg-white px-1 rounded text-xs">business_unit</code> en el payload — backfill pendiente</div>
                <div className="relative mt-2" style={{ height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden", maxWidth: 320 }}>
                  <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${unassigned_pct}%`, background: "#cbd5e1", borderRadius: 3 }}/>
                </div>
              </div>
              <div className="text-right">
                <div className="font-display font-extrabold" style={{ fontSize: 22, color: "#475569", letterSpacing: "-0.02em", lineHeight: 1 }}>{fmtN(unassigned)}</div>
                <div className="text-xs font-extrabold mt-1" style={{ color: "#94a3b8" }}>{unassigned_pct}%</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

